'use client';

import { FC, useState } from 'react';
import { Content, asText, isFilled } from '@prismicio/client';
import type { ImageField } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';
import HighlightedText from '@/components/HighlightedText/HighlightedText';

/**
 * Props for `FeatureHighlightsWithMedia`.
 */
export type FeatureHighlightsWithMediaProps =
  SliceComponentProps<Content.FeatureHighlightsWithMediaSlice>;

/**
 * Component for "FeatureHighlightsWithMedia" Slices.
 */
type AppMediaItem = { appview: ImageField<never> };
type DefaultPrimary = Extract<
  Content.FeatureHighlightsWithMediaSlice,
  { variation: 'default' }
>['primary'];
type SimplifiedPrimary = Extract<
  Content.FeatureHighlightsWithMediaSlice,
  { variation: 'simplified' }
>['primary'];
type DefaultFeatureItem = NonNullable<DefaultPrimary['features']>[number];
type SimplifiedFeatureItem = NonNullable<SimplifiedPrimary['features']>[number];

const FeatureHighlightsWithMedia: FC<FeatureHighlightsWithMediaProps> = ({
  slice,
}) => {
  const { section_heading } = slice.primary;
  // appmedia only exists on default variation; access defensively
  const appmedia =
    'appmedia' in slice.primary
      ? (slice.primary as { appmedia?: AppMediaItem[] }).appmedia
      : undefined;
  const isSimplified = slice.variation === 'simplified';

  // Filter out empty images
  const validImages: AppMediaItem[] = Array.isArray(appmedia)
    ? appmedia.filter((media): media is AppMediaItem =>
        Boolean(media && isFilled.image(media.appview)),
      )
    : [];
  const hasImages = validImages.length > 0;

  // State for current image index
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % validImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex(
      (prev) => (prev - 1 + validImages.length) % validImages.length,
    );
  };

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="text-white font-inter py-16"
      style={{
        background: isSimplified
          ? 'linear-gradient(180deg, #0F423A 0%, #0E1010 100%)'
          : 'linear-gradient(180deg, #0F3932 58.58%, #0E1110 100%)',
      }}
    >
      <div className="container mx-auto px-4 sm:px-10 lg:px-[174px]">
        {isSimplified ? (
          // Simplified variation: staircase feature cards only
          <div>
            {section_heading && (
              <h2 className="text-white font-inter font-medium text-[40px] leading-[120%] mb-10">
                <HighlightedText text={asText(section_heading) || ''} />
              </h2>
            )}
            {(slice.primary as SimplifiedPrimary).features &&
              (slice.primary as SimplifiedPrimary).features!.length > 0 &&
              (() => {
                const features =
                  (slice.primary as SimplifiedPrimary).features ?? [];
                const filtered = features.filter(
                  (f) => (f as SimplifiedFeatureItem).description,
                ) as SimplifiedFeatureItem[];
                return (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8 pb-[80px]">
                    {filtered.map(
                      (feature: SimplifiedFeatureItem, index: number) => {
                        return (
                          <div
                            key={index}
                            className={`rounded-xl bg-[linear-gradient(180deg,rgba(0,0,0,0.20)_0%,rgba(0,0,0,0)_100%)] shadow-[0_4.325px_64.871px_0_rgba(0,0,0,0.08)] backdrop-blur-[10px] p-6 w-full`}
                            style={{ transform: `translateY(${index * 24}px)` }}
                          >
                            <div className="text-white font-inter font-semibold text-[24px] leading-[140%]">
                              <HighlightedText
                                text={
                                  asText(
                                    (feature as SimplifiedFeatureItem)
                                      .description,
                                  ) || ''
                                }
                              />
                            </div>
                          </div>
                        );
                      },
                    )}
                  </div>
                );
              })()}
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
            {/* Left side - Section Heading */}
            <div className="lg:w-1/3 flex-shrink-0">
              {section_heading && (
                <h2 className="text-white font-inter font-medium text-[40px] leading-[120%]">
                  <HighlightedText text={asText(section_heading) || ''} />
                </h2>
              )}
            </div>

            {/* Right side - Features */}
            <div className="lg:w-2/3 flex flex-col gap-6">
              {(slice.primary as DefaultPrimary).features &&
                (slice.primary as DefaultPrimary).features!.length > 0 && (
                  <>
                    {((slice.primary as DefaultPrimary).features ?? []).map(
                      (feature: DefaultFeatureItem, index: number) => (
                        <div
                          key={index}
                          className="rounded-2xl bg-gradient-to-b from-[rgba(0,0,0,0.20)] to-[rgba(0,0,0,0.00)] shadow-[0_4.325px_64.871px_0_rgba(0,0,0,0.08)] backdrop-blur-[10px] p-6"
                        >
                          {feature.title && (
                            <h3 className="text-[#05F273] font-inter font-bold text-[24px] leading-[140%] mb-3">
                              <HighlightedText
                                text={asText(feature.title) || ''}
                              />
                            </h3>
                          )}
                          {feature.description && (
                            <div className="text-white font-inter font-normal text-[18px] leading-[140%]">
                              <HighlightedText
                                text={asText(feature.description) || ''}
                              />
                            </div>
                          )}
                        </div>
                      ),
                    )}
                  </>
                )}

              {/* Images - Single image with navigation */}
              {hasImages && (
                <div className="flex items-center justify-center mt-6">
                  {/* Left Arrow */}
                  {validImages.length > 1 && (
                    <button
                      onClick={prevImage}
                      className="flex-shrink-0 hover:opacity-80 transition-opacity mr-4"
                      aria-label="Previous image"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="46"
                        height="46"
                        viewBox="0 0 46 46"
                        fill="none"
                      >
                        <rect
                          width="46"
                          height="46"
                          transform="matrix(-1 0 0 1 46 0)"
                          fill="#0F423A"
                        />
                        <path
                          d="M21.697 13.9277C21.3889 13.6007 20.9711 13.417 20.5355 13.417C20.0999 13.417 19.6821 13.6007 19.374 13.9277L11.981 21.7776C11.673 22.1047 11.5 22.5483 11.5 23.0109C11.5 23.4734 11.673 23.917 11.981 24.2442L19.374 32.094C19.6838 32.4117 20.0988 32.5876 20.5296 32.5836C20.9603 32.5796 21.3724 32.3962 21.677 32.0727C21.9816 31.7493 22.1544 31.3118 22.1581 30.8544C22.1619 30.397 21.9963 29.9564 21.697 29.6274L17.2497 24.7553H32.8571C33.2928 24.7553 33.7107 24.5715 34.0188 24.2443C34.3269 23.9172 34.5 23.4735 34.5 23.0109C34.5 22.5482 34.3269 22.1045 34.0188 21.7774C33.7107 21.4502 33.2928 21.2664 32.8571 21.2664H17.2497L21.697 16.3943C22.005 16.0672 22.178 15.6236 22.178 15.161C22.178 14.6985 22.005 14.2549 21.697 13.9277Z"
                          fill="#002726"
                        />
                      </svg>
                    </button>
                  )}

                  {/* Single Image Display */}
                  <div
                    className="rounded-xl overflow-hidden"
                    style={{ width: '420px', height: '320px' }}
                  >
                    <PrismicNextImage
                      field={validImages[currentImageIndex].appview}
                      className="w-full h-full object-contain"
                      loading="lazy"
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 60vw, 40vw"
                    />
                  </div>

                  {/* Right Arrow */}
                  {validImages.length > 1 && (
                    <button
                      onClick={nextImage}
                      className="flex-shrink-0 hover:opacity-80 transition-opacity ml-4"
                      aria-label="Next image"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="46"
                        height="46"
                        viewBox="0 0 46 46"
                        fill="none"
                      >
                        <rect width="46" height="46" fill="#0F423A" />
                        <path
                          d="M24.303 13.9277C24.6111 13.6007 25.0289 13.417 25.4645 13.417C25.9001 13.417 26.3179 13.6007 26.626 13.9277L34.019 21.7776C34.327 22.1047 34.5 22.5483 34.5 23.0109C34.5 23.4734 34.327 23.917 34.019 24.2442L26.626 32.094C26.3162 32.4117 25.9012 32.5876 25.4704 32.5836C25.0397 32.5796 24.6276 32.3962 24.323 32.0727C24.0184 31.7493 23.8456 31.3118 23.8419 30.8544C23.8381 30.397 24.0037 29.9564 24.303 29.6274L28.7503 24.7553H13.1429C12.7072 24.7553 12.2893 24.5715 11.9812 24.2443C11.6731 23.9172 11.5 23.4735 11.5 23.0109C11.5 22.5482 11.6731 22.1045 11.9812 21.7774C12.2893 21.4502 12.7072 21.2664 13.1429 21.2664H28.7503L24.303 16.3943C23.995 16.0672 23.822 15.6236 23.822 15.161C23.822 14.6985 23.995 14.2549 24.303 13.9277Z"
                          fill="#002726"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default FeatureHighlightsWithMedia;
